import React, { useState, useEffect } from 'react'
import Papa from 'papaparse'
import { PlusIcon, ArrowLeftIcon, ArrowRightIcon, CloudArrowUpIcon } from '@heroicons/react/20/solid'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import Alarm from '../../components/Alarm'
import Spin from '../../components/Spin'
import { parseSecurely } from '../../libs/parse'
import { API, Auth } from 'aws-amplify'
import { apiPost } from '../../libs/apiHelper'
import { useAppContext } from '../../libs/context'
import { CAMPAIGNS } from '../../config/app'
import InvalidNumbersModal from '../../components/InvalidNumbersModal'
import ExcelClosingSummary from '../../components/ExcelClosingSummary'
import i18n from '../../i18n'
import { getFormatter, applyColumnMapper } from '../../config/uploadFormatters'
import FormatterMapper from '../../components/FormatterMapper'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Normalize a language value from the upload (CSV/JSON) into an ISO code.
// Accepts codes (es, ca, gl, eu, fr, en) and common aliases in ES/CA/EN/etc.
// Returns 'es' when missing/unknown so the flow defaults to Castilian.
const LANG_ALIASES = {
  es: 'es', spa: 'es', esp: 'es', spanish: 'es', castellano: 'es', castella: 'es', castellan: 'es', castellano_es: 'es',
  ca: 'ca', cat: 'ca', catalan: 'ca', catala: 'ca',
  gl: 'gl', gal: 'gl', gallego: 'gl', galego: 'gl', galician: 'gl',
  eu: 'eu', eus: 'eu', euskera: 'eu', euskara: 'eu', vasco: 'eu', basque: 'eu',
  fr: 'fr', fra: 'fr', frances: 'fr', francais: 'fr', french: 'fr',
  en: 'en', eng: 'en', ingles: 'en', english: 'en'
}
const normalizeLang = (raw) => {
  if (raw === undefined || raw === null) return 'es'
  const s = String(raw)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (!s) return 'es'
  return LANG_ALIASES[s] || 'es'
}

const formats = {
  basic: {
    csv: () => {
      return (
        <div className="mt-3 p-4 rounded-md border border-gray-300 bg-gray-50 font-mono text-sm">
          <p className="text-gray-600">phone</p>
          <p>123456789</p>
          <p>987654321</p>
          <p className="text-gray-400">...</p>
        </div>
      )
    },
    json: () => {
      return (
        <div className="mt-3 p-4 rounded-md border border-gray-300 bg-gray-50 font-mono text-sm">
          <p>{'[{'}</p>
          <p className="pl-4">"phone": "123456789"</p>
          <p>{'},...]'}</p>
        </div>
      )
    }
  },
  withName: {
    csv: () => {
      return (
        <div className="mt-3 p-4 rounded-md border border-gray-300 bg-gray-50 font-mono text-sm">
          <p className="text-gray-600">name,phone</p>
          <p>John,123456789</p>
          <p>Alex,987654321</p>
          <p className="text-gray-400">...</p>
        </div>
      )
    },
    json: () => {
      return (
        <div className="mt-3 p-4 rounded-md border border-gray-300 bg-gray-50 font-mono text-sm">
          <p>{'[{'}</p>
          <p className="pl-4">"name": "John",</p>
          <p className="pl-4">"phone": "123456789"</p>
          <p>{'},...]'}</p>
        </div>
      )
    }
  },
  unknown: {
    csv: () => {
      return (
        <div className="mt-3 p-4 rounded-md border border-gray-300 bg-gray-50 font-mono text-sm">
          <p className="text-gray-600">phone, ...</p>
          <p>123456789, ...</p>
          <p>987654321, ...</p>
          <p className="text-gray-400">...</p>
        </div>
      )
    },
    json: () => {
      return (
        <div className="mt-3 p-4 rounded-md border border-gray-300 bg-gray-50 font-mono text-sm">
          <p>{'[{'}</p>
          <p className="pl-4">"phone": "123456789",</p>
          <p className="pl-4">"xxx": "xxx",</p>
          <p className="pl-4 text-gray-400">...</p>
          <p>{'},...]'}</p>
        </div>
      )
    }
  }
}

const getTexts = (object) => {
  const keysToSearch = [
    'textMessage',
    'title',
    'description',
    'text',
    'expectedAnswer'
  ]
  let results = []

  // Recursive function to search in the object
  function recursiveSearch(obj) {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        // Check if the key is in the list of keys to search
        if (keysToSearch.includes(key)) {
          results.push(obj[key])
        }

        // If the value of the key is an object, perform a recursive search
        if (typeof obj[key] === 'object') {
          recursiveSearch(obj[key])
        }
      }
    }
  }

  // Start the recursive search
  recursiveSearch(object)

  return results
}

const getVariablesFormText = (text) => {
  const regex = /\{(.*?)\}/g
  const matches = []
  let match

  while ((match = regex.exec(text)) !== null) {
    matches.push(match[1])
  }

  return matches
}

const Upload = () => {
  const [alarmContent, setAlarmContent] = useState({})
  const [invalidContacts, setInvalidContacts] = useState([])
  const [duplicatedContacts, setDuplicatedContacts] = useState([])
  // Detalle fila a fila de los contactos descartados: { row, phone, name, reason }.
  // `reason` es una clave de i18n (removal_reason_*) para poder traducirla.
  const [removedContacts, setRemovedContacts] = useState([])
  const [invalidModal, setInvalidModal] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const [campaignMissingVariables, setCampaignMissingVariables] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [excelProcessing, setExcelProcessing] = useState(false)
  const hiddenFileInput = React.useRef(null)

  const {
    onStepClick,
    setContacts,
    stepsData,
    setStepsData,
    stat,
    setStat,
    selectedCampaign,
    contacts,
    excelJob,
    setExcelJob
  } = useAppContext()

  const schema = selectedCampaign?.schema || 'basic'

  // CaixaBank recobros templates accept the daily .xlsx/.xlsm directly: the file
  // is sent to /process-excel, which creates the campaign and closes paid flows
  // server-side (no manual "Send" step). Detect the flow from the init element.
  const flowName =
    (selectedCampaign?.initElement || '').split('-init-')[0] ||
    selectedCampaign?.name ||
    ''
  // Formatter propio de la template (config/uploadFormatters.js). Solo los que
  // tienen `excel: true` (recobros) aceptan el Excel diario; preventivo tiene su
  // propio CSV y se mapea con `columns`.
  const formatter = getFormatter(selectedCampaign)
  const isCaixaTemplate = !!formatter?.excel

  const getAllVariables = () => {
    if (selectedCampaign && selectedCampaign.elements) {
      const elements = typeof selectedCampaign.elements === 'string' 
        ? JSON.parse(selectedCampaign.elements) 
        : selectedCampaign.elements
      const campaignTexts = getTexts(elements)
      const variables = [].concat(
        ...campaignTexts.map((text) => getVariablesFormText(text))
      )
      return variables
    }
    return []
  }
  const CAMPAIGN_VARIABLES = getAllVariables()

  const getContactMissingVariables = (contact) => {
    const contactVariables = Object.keys(contact)
    const missingVariables = CAMPAIGN_VARIABLES.filter(
      (variable) => !contactVariables.includes(variable)
    )
    return missingVariables
  }

  const updateMissingVariables = (contacts) => {
    let _union = new Set()
    for (let contact of contacts) {
      for (let elem of getContactMissingVariables(contact)) {
        _union.add(elem)
      }
    }
    const missingVariables = Array.from(_union)
    console.log(missingVariables)
    setCampaignMissingVariables(missingVariables)
  }

  const handleClick = (event) => {
    hiddenFileInput.current.click()
  }

  const getInfo = async () => {
    const user = await Auth.currentAuthenticatedUser()
    const userFullInfo = await API.get('rcs-api', '/user', {
      headers: {
        Authorization: user.signInUserSession.idToken.jwtToken
      }
    })
    const prefix = userFullInfo.countryCode ? userFullInfo.countryCode : '93'
    setUserInfo(userFullInfo)
  }

  // Normaliza una fila descartada al formato de la tabla de descartes.
  // `original` es el teléfono tal y como venía en el fichero, antes de limpiar.
  const buildRemoval = (contact, original, reason) => ({
    row: typeof contact.key === 'number' ? contact.key + 2 : null,
    phone: original || '',
    name: contact.name || '',
    reason
  })

  const cleanContacts = (contacts) => {
    let prefix = '00'

    if (userInfo.countryCode) {
      prefix = userInfo.countryCode
    }

    switch (prefix) {
      case '36':
        return hungarianFormat(contacts)

      default:
        console.log(selectedCampaign)
        let phonesUsed = []
        let invalids = []
        let duplicated = []
        let removed = []
        const cleanPhones = contacts.map((contact) => {
          const original = contact.phone
          if (!contact.phone || contact.phone.length == 0) {
            invalids.push(contact)
            removed.push(buildRemoval(contact, original, 'empty_phone'))
            return null
          }
          contact.phone = contact.phone
            .substring(contact.phone.startsWith('00') ? 2 : 0)
            .replace(/\D/g, '')
          // Valores tipo "SIN TELEFONO" o "-" se quedan vacíos tras limpiar y el
          // backend (Joi) rechaza toda la campaña con un 502: descartarlos aquí.
          if (contact.phone.length == 0) {
            invalids.push(contact)
            removed.push(buildRemoval(contact, original, 'empty_phone'))
            return null
          }
          // El prefijo se antepone ANTES de comparar duplicados: si no, la misma
          // persona escrita como `659334568` y como `34659334568` pasa el filtro
          // aquí y la acaba deduplicando splitCampaign en backend, en silencio.
          if (contact.phone.length == 9) {
            contact.phone = '34' + contact.phone
          }
          if (phonesUsed.includes(contact.phone)) {
            duplicated.push(contact)
            removed.push(buildRemoval(contact, original, 'duplicated'))
            return null
          }

          phonesUsed.push(contact.phone)
          return contact
        })
        setInvalidContacts(invalids)
        setDuplicatedContacts(duplicated)
        setRemovedContacts(removed)
        if (invalids.length != 0 || duplicated.length != 0) {
          console.log('invalids')
          setInvalidModal(true)
        }
        return cleanPhones.filter((contact) => contact !== null)
    }
  }

  const hungarianFormat = (contacts) => {
    let invalid = []
    let duplicated = []
    let phonesUsed = []
    let removed = []
    const cleanPhones = contacts.map((contact) => {
      const original = contact.phone
      if (!contact.phone || contact.phone.length == 0) {
        invalid.push(contact)
        removed.push(buildRemoval(contact, original, 'empty_phone'))
        return null
      }
      const onlyDigits = contact.phone
        .substring(contact.phone.startsWith('00') ? 2 : 0)
        .replace(/\D/g, '')
      let cleanPhone = onlyDigits

      if (onlyDigits.length >= 10 && onlyDigits.length <= 11) {
        if (onlyDigits.startsWith('06')) {
          cleanPhone = '36' + onlyDigits.substr(2)
        } else if (onlyDigits.startsWith('36')) {
          cleanPhone = onlyDigits
        } else {
          invalid.push(contact)
          removed.push(buildRemoval(contact, original, 'bad_country_prefix'))
          return null
        }
      } else if (onlyDigits.length >= 8 && onlyDigits.length <= 9) {
        cleanPhone = '36' + onlyDigits
      } else {
        invalid.push(contact)
        removed.push(buildRemoval(contact, original, 'bad_length'))
        return null
      }
      if (!/^36(?:20|30|31|50|70)\d*$/.test(cleanPhone)) {
        invalid.push(contact)
        removed.push(buildRemoval(contact, original, 'not_mobile'))
        return null
      }
      //check if duplicated
      if (phonesUsed.includes(cleanPhone)) {
        duplicated.push(contact.phone)
        removed.push(buildRemoval(contact, original, 'duplicated'))
        return null
      }
      phonesUsed.push(cleanPhone)
      contact.phone = cleanPhone
      return contact
    })
    console.log('invalid', invalid)
    setInvalidContacts(invalid)
    setDuplicatedContacts(duplicated)
    setRemovedContacts(removed)
    if (invalid.length != 0 || duplicated.length != 0) {
      setInvalidModal(true)
    }
    return cleanPhones.filter((contact) => contact !== null)
  }
  // Excel CaixaBank: se procesa con dryRun (NO envía ni cierra nada) para obtener
  // el resumen — a quién se enviará (ENTRADAS HOY) y a quién se cerrará el flujo
  // (duplicados + SEGUIMIENTO REG_TRA_7D=1). Con eso rellenamos `contacts` y
  // `excelJob` y seguimos por el MISMO flujo que un CSV: paso "Send", donde se
  // puede lanzar ya o programar. El envío/cierre real lo hace /process-excel al
  // pulsar Lanzar/Programar (Send.js), no aquí.
  const processExcelUpload = async (file) => {
    setExcelProcessing(true)
    setExcelJob(null)
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(r.result)
        r.onerror = () => reject(new Error('Error al leer el archivo'))
        r.readAsDataURL(file)
      })
      const base64 = String(dataUrl).replace(/^data:[^,]*,/, '')

      const res = await apiPost('/process-excel', {
        body: {
          source: { type: 'base64', data: base64 },
          flow: flowName,
          dryRun: true
        }
      })
      let summary = res?.summary || res || {}

      // Guard de relanzamiento: estas ENTRADAS ya se lanzaron en otra campaña
      // (cada init se factura). Solo se relanzan si el usuario lo confirma
      // (force); si no, se procesa el Excel sin lanzar (se aplican los cierres
      // del SEGUIMIENTO).
      let force = false
      if (summary.skippedReason) {
        const prev = summary.previousLaunch || {}
        const when = prev.timestamp
          ? new Date(prev.timestamp).toLocaleString('es-ES')
          : ''
        const msg =
          summary.skippedReason === 'entries_already_launched'
            ? `Las entradas de este Excel ya se enviaron en la campaña ${summary.previousCampaignId}` +
              `${when ? ` (${when})` : ''}: ${prev.alreadyLaunchedPhones || 0} de ` +
              `${prev.entriesPhones || 0} teléfonos ya recibieron el mensaje inicial.\n\n` +
              '¿Quieres volver a enviarlas igualmente?'
            : 'No se pudo comprobar si estas entradas ya se enviaron antes.\n\n' +
              '¿Quieres enviarlas igualmente?'
        force = window.confirm(msg)
        if (force) {
          // Repetir la preview con force para tener los cierres de duplicados.
          const forced = await apiPost('/process-excel', {
            body: {
              source: { type: 'base64', data: base64 },
              flow: flowName,
              dryRun: true,
              force: true
            }
          })
          summary = forced?.summary || forced || {}
        }
      }

      if ((summary.newContacts || 0) > 0 && (!summary.skippedReason || force)) {
        // Flujo unificado: los contactos de ENTRADAS HOY se tratan como los de un
        // CSV (mostrar recuento, continuar a Send).
        const mapped = (summary.newContactsList || []).map((c, i) => ({
          key: i,
          ...c
        }))
        setContacts(mapped)
        setStat({ ...stat, users: mapped.length })
        setCampaignMissingVariables([])
        const _stepsData = [...stepsData]
        const step2 = _stepsData.find(({ id }) => id === 2)
        if (step2) step2.completed = true
        setStepsData(_stepsData)
        setExcelJob({
          base64,
          fileName: file.name || '',
          flow: flowName,
          force,
          preview: summary
        })
      } else {
        // Sin contactos nuevos (solo SEGUIMIENTO): no hay nada que enviar ni
        // programar, así que aplicamos los cierres en el acto.
        const real = await apiPost('/process-excel', {
          body: { source: { type: 'base64', data: base64 }, flow: flowName }
        })
        const rs = real?.summary || real || {}
        const closedTotal =
          (rs.flowsClosed || 0) + (rs.duplicateFlowsClosed || 0)
        const skippedMsg =
          rs.skippedReason === 'entries_already_launched'
            ? ` Entradas NO enviadas: ya se lanzaron en la campaña ${rs.previousCampaignId}.`
            : rs.skippedReason
              ? ' Entradas NO enviadas: no se pudo comprobar si ya se lanzaron.'
              : ''
        setAlarmContent({
          type: rs.errors?.length ? 'alarm' : 'success',
          message: `Excel procesado. Sin contactos nuevos a enviar.${skippedMsg} Flujos cerrados: ${closedTotal}.`,
          closable: true
        })
      }
    } catch (err) {
      console.error('[Upload] Error procesando Excel:', err)
      const apiMsg = err?.response?.data?.error
      setAlarmContent({
        type: 'error',
        message: apiMsg || err.message || 'Error al procesar el Excel.',
        closable: true
      })
    } finally {
      setExcelProcessing(false)
    }
  }

  const readFile = async (e) => {
    e.preventDefault()
    const file = e.target.files[0]
    if (!file) {
      setAlarmContent({
        type: 'error',
        message: 'No se seleccionó ningún archivo',
        closable: true
      })
      return
    }
    
    const reader = new FileReader()
    // Detectar tipo por extensión también (Chrome/macOS a veces no pone MIME type correcto)
    const fileName = file.name || ''
    const isJson = file.type === 'application/json' || fileName.toLowerCase().endsWith('.json')
    const isCsv = file.type === 'text/csv' || fileName.toLowerCase().endsWith('.csv')
    const isExcel = /\.(xlsx|xlsm|xls)$/i.test(fileName) ||
      file.type.includes('spreadsheet') || file.type.includes('ms-excel')

    if (isExcel) {
      if (!isCaixaTemplate) {
        setAlarmContent({
          type: 'error',
          message: formatter
          ? `La plantilla ${formatter.label} no admite Excel. Usa ${formatter.accepts.map((a) => '.' + a).join(', ')}.`
          : 'Los archivos Excel solo se admiten para plantillas CaixaBank (recobros). Para otras plantillas usa CSV o JSON.',
          closable: true
        })
        return
      }
      await processExcelUpload(file)
      return
    }

    reader.onload = async (e) => {
      try {
        const text = e.target.result
        let campaignContacts
        if (isJson) {
          campaignContacts = parseSecurely(text)
          if (!Array.isArray(campaignContacts)) {
            throw new Error('El archivo JSON debe ser un array de contactos')
          }
        } else if (isCsv) {
          campaignContacts = Papa.parse(text, formatter?.columns && !formatter.excel
            ? { header: true, skipEmptyLines: 'greedy' }
            : { header: true }).data
        } else {
          throw new Error('Formato no soportado. Usa CSV o JSON.')
        }
        
        if (!campaignContacts || campaignContacts.length === 0) {
          throw new Error('El archivo está vacío o no tiene contactos válidos')
        }
        
        // Formatter con mapper de columnas propio (p. ej. preventivo): traduce las
        // cabeceras del cliente a variables y sigue por la misma limpieza de teléfonos.
        if (formatter && !formatter.excel) {
          const { rows, missing } = applyColumnMapper(formatter, campaignContacts)
          if (missing.length) {
            throw new Error(
              `Formato ${formatter.label}: faltan columnas obligatorias (${missing.join(', ')}). Esperado: ${formatter.columns.map((c) => c.source).join(', ')}`
            )
          }
          campaignContacts = rows.map((el, i) => {
            const o = { key: i }
            Object.keys(el).forEach((k) => {
              o[k] = k === 'lang'
                ? el[k]
                : el[k].normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            })
            o.lang = normalizeLang(el.lang)
            return o
          })
        } else {
        const varsMaps = CAMPAIGNS[schema].maps
        campaignContacts = campaignContacts.map((el, i) => {
          const o = { key: i }
          if (schema == 'unknown') {
            let swapMap = {}
            for (let key in varsMaps) {
              swapMap[varsMaps[key]] = key
            }
            Object.keys(el).forEach((k) => {
              const val = el[k]
              if (val !== undefined && val !== null) {
                o[swapMap[k] ? swapMap[k] : k] = String(val)
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
              }
            })
          } else {
            Object.keys(varsMaps).forEach((k) => {
              const val = el[varsMaps[k]]
              if (val !== undefined && val !== null) {
                o[k] = String(val)
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
              }
            })
          }
          if ('dueDate' in o) {
            o['dueDate'] = o['dueDate'].split('-').join('/')
          }
          // Resolve i18n language for this contact from any of the accepted
          // header names (lang, idioma, language, locale). Missing → 'es'.
          o.lang = normalizeLang(
            el.lang !== undefined ? el.lang :
            el.idioma !== undefined ? el.idioma :
            el.language !== undefined ? el.language :
            el.locale !== undefined ? el.locale : undefined
          )
          return o
        })
        }
        const validContacts = cleanContacts(campaignContacts)
        updateMissingVariables(validContacts)
        console.log('[Upload] Contactos válidos:', validContacts)
        setContacts(validContacts)
        const _stat = { ...stat }
        _stat.users = validContacts.length
        setStat(_stat)
        const _stepsData = [...stepsData]
        _stepsData.find(({ id }) => id === 2).completed = true
        setStepsData(_stepsData)
        
        if (validContacts.length === 0) {
          setAlarmContent({
            type: 'error',
            message: i18n.t('file_not_valid') || 'No se encontraron contactos válidos. Revisa el formato.',
            closable: true
          })
        }
      } catch (err) {
        console.error('[Upload] Error al procesar archivo:', err)
        setAlarmContent({
          type: 'error',
          message: err.message || 'Error al procesar el archivo. Revisa el formato.',
          closable: true
        })
      }
    }
    
    reader.onerror = () => {
      setAlarmContent({
        type: 'error',
        message: 'Error al leer el archivo',
        closable: true
      })
    }
    
    if (isJson || isCsv) {
      reader.readAsText(file)
    } else {
      setAlarmContent({
        type: 'error',
        message: 'Formato de archivo no válido. Usa .json o .csv',
        closable: true
      })
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      const isExcelDrop = /\.(xlsx|xlsm|xls)$/i.test(file.name)
      if (file.type === 'application/json' || file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.json') || isExcelDrop) {
        readFile({ target: { files: [file] }, preventDefault: () => {} })
      } else {
        setAlarmContent({
          type: 'error',
          message: i18n.t('invalid_file_format') || 'Invalid file format. Please use CSV or JSON.',
          closable: true
        })
      }
    }
  }

  const handleBackClick = () => {
    onStepClick({ id: 1, href: 'select' }, true)
  }

  const handleNextClick = () => {
    if (contacts.length === 0) {
      setAlarmContent({
        type: 'error',
        message: i18n.t('upload_contacts_first') || 'Please upload contacts first',
        closable: true
      })
      return
    }
    onStepClick({
      id: 3,
      href: 'send'
    }, true)
  }

  const handleResetContacts = () => {
    setContacts([])
    setExcelJob(null)
    setStat({ ...stat, users: '0' })
    setCampaignMissingVariables([])
    setInvalidContacts([])
    setDuplicatedContacts([])
    setRemovedContacts([])
    if (hiddenFileInput.current) hiddenFileInput.current.value = ''
    // Desmarcar paso 2 como completado
    const _stepsData = [...stepsData]
    _stepsData.find(({ id }) => id === 2).completed = false
    setStepsData(_stepsData)
  }

  useEffect(() => {
    getInfo()
  }, [])

  const hasContacts = stat.users !== '0' && parseInt(stat.users) > 0
  const canProceed = hasContacts && campaignMissingVariables.length === 0

  // Protección: si no hay selectedCampaign, mostrar mensaje de error
  if (!selectedCampaign) {
    return (
      <div className="py-12">
        <div className="w-full max-w-md mx-auto text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {i18n.t('no_template_selected') || 'No template selected'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {i18n.t('select_template_first') || 'Please select a template first'}
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => onStepClick({ id: 1, href: 'select' }, true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-dark hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
            >
              <ArrowLeftIcon className="-ml-1 mr-2 h-4 w-4" />
              {i18n.t('go_to_select_template') || 'Go to select template'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-6">
      <Alarm {...alarmContent} setAlarmContent={setAlarmContent} />
      <InvalidNumbersModal
        open={invalidModal}
        setOpen={setInvalidModal}
        invalidContacts={invalidContacts}
        duplicatedContacts={duplicatedContacts}
      />

      <div className="w-full max-w-2xl mx-auto">
        {/* Header con información del template seleccionado */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <span className="font-medium">{i18n.t('selected_template') || 'Selected template'}:</span>{' '}
                {selectedCampaign.name}
              </p>
            </div>
          </div>
        </div>

        {isCaixaTemplate && !excelProcessing && !excelJob && !hasContacts && (
          <div className="mb-6 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            Plantilla CaixaBank: al subir el Excel diario (hoja <span className="font-medium">ENTRADAS HOY</span>) se cargan los contactos como en un CSV y se muestra <span className="font-medium">a quién se cerrará el flujo</span> (duplicados + hoja <span className="font-medium">SEGUIMIENTO</span> con <span className="font-medium">REG_TRA_7D=1</span>). Nada se envía hasta que pulses <span className="font-medium">Lanzar/Programar</span> en el paso siguiente.
          </div>
        )}
        {excelProcessing ? (
          <div className="text-center py-16">
            <div className="flex justify-center">
              <Spin type="lightBg" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              Analizando Excel de CaixaBank…
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Calculando el resumen. No se envía nada todavía. Puede tardar unos segundos.
            </p>
          </div>
        ) : !hasContacts ? (
          // Estado: Sin contactos - mostrar zona de upload
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              {i18n.t('upload_contacts_title') || 'Upload your contacts'}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {i18n.t('formats_acepted')}
            </p>

            {/* Zona de Drag & Drop */}
            <div
              className={classNames(
                isDragging ? 'border-blue-dark bg-blue-50' : 'border-gray-300 bg-white',
                'border-2 border-dashed rounded-lg p-8 transition-colors duration-150 cursor-pointer hover:border-gray-400'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClick}
            >
              <CloudArrowUpIcon className={classNames(
                isDragging ? 'text-blue-dark' : 'text-gray-400',
                'mx-auto h-12 w-12'
              )} />
              <p className="mt-4 text-sm font-medium text-gray-900">
                {i18n.t('drag_drop_file') || 'Drag and drop your file here'}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {i18n.t('or_click_to_browse') || 'or click to browse'}
              </p>
              <p className="mt-2 text-xs text-gray-400">
                {formatter
                  ? formatter.accepts.map((a) => a.toUpperCase()).join(', ') + ` · ${formatter.label}`
                  : 'CSV, JSON'}
              </p>
            </div>

            <input
              type="file"
              accept={isCaixaTemplate
                ? '.json,.csv,.xlsx,.xlsm,.xls,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                : '.json,.csv,application/json,text/csv'}
              style={{ display: 'none' }}
              ref={hiddenFileInput}
              onChange={readFile}
            />

            {/* Formatos de ejemplo: si la template tiene formatter, su mapper */}
            {formatter ? (
              <div className="mt-8">
                <FormatterMapper formatter={formatter} />
              </div>
            ) : (
            <div className="mt-8">
              <p className="text-sm font-medium text-gray-700 mb-4">
                {i18n.t('file_format_examples') || 'File format examples'}:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="text-left">
                  <p className="font-medium text-grey-blue text-sm">
                    {i18n.t('csv_format')}
                  </p>
                  {formats[schema]?.csv()}
                </div>
                <div className="text-left">
                  <p className="font-medium text-grey-blue text-sm">
                    {i18n.t('json_format')}
                  </p>
                  {formats[schema]?.json()}
                </div>
              </div>
            </div>
            )}

            {/* Botón Atrás */}
            <div className="mt-8 flex justify-start">
              <button
                type="button"
                onClick={handleBackClick}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
              >
                <ArrowLeftIcon className="-ml-1 mr-2 h-4 w-4" />
                {i18n.t('back') || 'Back'}
              </button>
            </div>
          </div>
        ) : (
          // Estado: Con contactos - mostrar resumen
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              {i18n.t('contacts_uploaded') || 'Contacts uploaded'}
            </h2>

            {/* Card con estadísticas */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-md bg-green-500 p-3">
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.users}</p>
                </div>
              </div>
              {excelJob && (
                <>
                  {excelJob.fileName && (
                    <p className="mt-3 text-xs text-gray-400">
                      {excelJob.fileName} · hojas:{' '}
                      {(excelJob.preview?.sheetsFound || []).join(', ') || '—'}
                    </p>
                  )}
                  <ExcelClosingSummary preview={excelJob.preview} />
                </>
              )}
            </div>

            {/* Alerta de variables faltantes */}
            {campaignMissingVariables.length > 0 && (
              <div className="mt-4 rounded-md bg-yellow-50 border border-yellow-200 p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      {i18n.t('be_warmed') || 'Warning'}
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>{i18n.t('missing_variables') || 'Missing variables in your file'}:</p>
                      <p className="mt-1 font-semibold">{campaignMissingVariables.join(', ')}</p>
                      <p className="mt-2">{i18n.t('check_the_file') || 'Please check your file and make sure all required variables are included.'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Información de contactos inválidos */}
            {(invalidContacts.length > 0 || duplicatedContacts.length > 0) && (
              <div className="mt-4 rounded-md bg-orange-50 border border-orange-200 p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-400 flex-shrink-0" />
                  <div className="ml-3 text-sm text-orange-700">
                    {invalidContacts.length > 0 && (
                      <p>{i18n.t('invalid_contacts_removed') || 'Invalid contacts removed'}: {invalidContacts.length}</p>
                    )}
                    {duplicatedContacts.length > 0 && (
                      <p>{i18n.t('duplicated_contacts_removed') || 'Duplicated contacts removed'}: {duplicatedContacts.length}</p>
                    )}
                    {removedContacts.length > 0 && (
                      <details className="mt-2 group">
                        <summary className="cursor-pointer font-medium text-orange-800 select-none">
                          {i18n.t('see_removed_detail') || 'Ver detalle'} ({removedContacts.length})
                        </summary>
                        <div className="mt-2 max-h-64 overflow-y-auto rounded border border-orange-200 bg-white">
                          <table className="min-w-full text-xs">
                            <thead className="sticky top-0 bg-orange-100 text-orange-900">
                              <tr>
                                <th className="px-2 py-1 text-left font-semibold">{i18n.t('row') || 'Fila'}</th>
                                <th className="px-2 py-1 text-left font-semibold">{i18n.t('phone') || 'Teléfono'}</th>
                                <th className="px-2 py-1 text-left font-semibold">{i18n.t('name') || 'Nombre'}</th>
                                <th className="px-2 py-1 text-left font-semibold">{i18n.t('reason') || 'Motivo'}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-orange-100 text-gray-700">
                              {removedContacts.map((r, i) => (
                                <tr key={i}>
                                  <td className="px-2 py-1 tabular-nums">{r.row ?? '-'}</td>
                                  <td className="px-2 py-1 font-mono">{r.phone || <span className="italic text-gray-400">{i18n.t('empty') || 'vacío'}</span>}</td>
                                  <td className="px-2 py-1">{r.name || '-'}</td>
                                  <td className="px-2 py-1">{i18n.t(`removal_reason_${r.reason}`) || r.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Área de acción con botón siguiente prominente */}
            <div className={classNames(
              'mt-6 p-4 rounded-lg border-2 transition-all duration-200',
              canProceed 
                ? 'bg-green-50 border-green-300' 
                : 'bg-yellow-50 border-yellow-300'
            )}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center">
                  {canProceed ? (
                    <>
                      <svg className="h-5 w-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-green-800">
                        <span className="font-semibold">{stat.users}</span> {i18n.t('contacts_ready') || 'contacts ready to send'}
                      </span>
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                      <span className="text-sm text-yellow-800">
                        {i18n.t('fix_missing_variables') || 'Fix missing variables to continue'}
                      </span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  disabled={!canProceed}
                  onClick={handleNextClick}
                  className={classNames(
                    canProceed
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed',
                    'w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150'
                  )}
                >
                  {i18n.t('continue') || 'Continuar'}
                  <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Botones secundarios */}
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleBackClick}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
              >
                <ArrowLeftIcon className="-ml-1 mr-2 h-4 w-4" />
                {i18n.t('back') || 'Back'}
              </button>
              <button
                type="button"
                onClick={handleResetContacts}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
              >
                {i18n.t('upload_different_file') || 'Upload different file'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Upload
